<script setup lang="ts">
import { computed, type FunctionalComponent } from 'vue';
import { CheckmarkIcon } from '@blpde/nx-icons';
import Minus from 'lucide-vue-next/dist/esm/icons/minus.js';
import { useInputSaveStatus } from './composables/useInputSaveStatus';
import NxSaveStateBadge from './NxSaveStateBadge.vue';

// TODO: use a model
const props = defineProps<{
  disabled?: boolean;
  /**
   * The control's accessible name, for a checkbox with no visible label — one
   * per row of a table column, where the header says WHAT and the row has to say
   * WHICH.
   *
   * A prop and not an attribute: this component's root is the wrapping `<span>`,
   * so an `aria-label` handed in from outside lands there instead of on the
   * `role="checkbox"` button, where nothing reads it.
   */
  ariaLabel?: string;
  /**
   * `ribbon` is the office ribbon's checkbox (Write's „Anzeigen“ group): a box
   * of the small icon size instead of 1.25em, so a column of them fits the
   * ribbon's fixed height.
   */
  size?: 'default' | 'ribbon';
  /**
   * Mixed: the box stands for several values that differ (the bold of a
   * multi-cell selection where only some cells are bold). Shows a dash and
   * `aria-checked="mixed"` regardless of the model. A click toggles the model
   * as usual; the host clears `indeterminate` once the user has decided.
   */
  indeterminate?: boolean;
  /**
   * Sits the box in a cell one text-input box tall (height `s`), centred, so a
   * checkbox in a form row lines up with the inputs beside it and a row of
   * checkboxes is as tall as a row of inputs.
   */
  alignWithInputs?: boolean;
}>();

// Without `alignWithInputs` the box renders with no cell around it, exactly as
// before. Attributes go onto `.nx-checkbox-root` either way, never onto the
// cell, so a consumer's class or `data-*` keeps landing where it always did.
// The one root span, not the slot's array: a fragment would make the
// component's `$el` an empty text node.
const NoCell: FunctionalComponent = (_, { slots }) => slots.default?.()[0];
NoCell.inheritAttrs = false;
defineOptions({ inheritAttrs: false });

const checkedModel = defineModel<boolean>({ required: true });

// Save status (loading/success/error) from an ancestor NxLoadingWrapper.
const {
  visualState,
  errorMessage,
  fadeOut,
  srText,
  ariaBindings,
  disabled: statusDisabled,
} = useInputSaveStatus();
const isDisabled = computed(() => props.disabled || statusDisabled.value);
</script>

<template>
  <component
    :is="props.alignWithInputs ? 'span' : NoCell"
    :class="props.alignWithInputs ? 'nx-checkbox-input-cell' : undefined"
  >
    <span class="nx-checkbox-root" v-bind="$attrs">
      <button
        type="button"
        role="checkbox"
        v-bind="ariaBindings"
        :aria-label="props.ariaLabel"
        :aria-checked="props.indeterminate ? 'mixed' : checkedModel"
        :disabled="isDisabled"
        :data-save-status="visualState"
        :class="[
          'nx-checkbox',
          {
            checked: checkedModel || props.indeterminate,
            indeterminate: props.indeterminate,
            disabled: isDisabled,
            'size-ribbon': props.size === 'ribbon',
          },
        ]"
        @click="checkedModel = !checkedModel"
      >
        <Minus
          v-if="props.indeterminate"
          :class="props.size === 'ribbon' ? 'nxt-icon-xs' : 'nxt-icon-md'"
          color="var(--nxt-checkbox-fg)"
          aria-hidden="true"
        />
        <CheckmarkIcon
          v-else-if="checkedModel"
          filled
          :class="props.size === 'ribbon' ? 'nxt-icon-xs' : 'nxt-icon-md'"
          color="var(--nxt-checkbox-fg)"
        />
      </button>
      <NxSaveStateBadge
        class="save-badge"
        :state="visualState"
        :error-message="errorMessage"
        :fade-out="fadeOut"
      />
      <span class="nx-sr-only" aria-live="polite">{{ srText }}</span>
    </span>
  </component>
</template>

<style scoped>
.nx-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25em;
  height: 1.25em;
  padding: 0;
  border: var(--nxt-hairline) solid var(--nxt-checkbox-border);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-checkbox-bg);
  cursor: pointer;
  outline: none;
  transition:
    background-color 0.15s,
    border-color 0.15s,
    box-shadow 0.15s;

  &:focus-visible {
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-checkbox-ring);
  }

  &:hover:not(:disabled) {
    border-color: var(--nxt-checkbox-border-hover);
  }

  &.checked:focus-visible {
    box-shadow:
      0 0 0 var(--nxt-hairline) var(--nxt-checkbox-bg),
      0 0 0 calc(var(--nxt-size-unit) * 0.125) var(--nxt-checkbox-ring);
  }

  &.checked {
    background-color: var(--nxt-checkbox-bg-checked);
    border-color: var(--nxt-checkbox-bg-checked);

    /* I-595 gap (#555): no dedicated --nxt-checkbox-bg-checked-hover token yet;
       falling back to the closest role token (brand hover). */
    &:hover:not(:disabled) {
      background-color: var(--nxt-brand-bg-hover);
      border-color: var(--nxt-brand-bg-hover);
    }
  }

  &.disabled {
    cursor: not-allowed;
    background-color: var(--nxt-checkbox-bg-disabled);
    border-color: var(--nxt-border);
  }

  /* Checked + disabled: same muted fill as unchecked-disabled (not brand), and
     the checkmark (CheckmarkIcon reads --nxt-checkbox-fg inline) follows via
     the redefined custom property instead of the brand color. */
  &.checked.disabled {
    background-color: var(--nxt-checkbox-bg-disabled);
    border-color: var(--nxt-checkbox-bg-disabled);
    --nxt-checkbox-fg: var(--nxt-input-fg-disabled);
  }
}

/* The ribbon's box: a size token, not em — the ribbon sets its own type on
   every button inside it, and a column of four must fit its fixed height. */
.nx-checkbox.size-ribbon {
  flex: 0 0 auto;
  width: var(--nxt-icon-size-sm);
  height: var(--nxt-icon-size-sm);
  min-height: 0;
}

/* `alignWithInputs`: one text-input box tall — the box of Inputs/style.css at
   height `s`: one control line at the small font size, plus its block padding
   and hairline border on both sides. The cell carries the height so the root
   keeps hugging the box and the save badge stays on its corner. */
.nx-checkbox-input-cell {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: calc(1lh + 2 * var(--nxt-space-xs) + 2 * var(--nxt-hairline));
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-control);
}

/* --- I-422 save status: corner badge ---
   The root hugs the checkbox (inline-flex, same footprint as the bare button —
   e.g. inside NxCheckboxField's flex row) and anchors the absolutely positioned
   badge; the checked/unchecked glyph itself stays untouched, so "checked" and
   "saved" can never be confused. */
.nx-checkbox-root {
  position: relative;
  display: inline-flex;
  /* Hug the box even when a parent (flex/grid) stretches the root — the corner
     badge anchors to this box, so it must match the control. */
  width: fit-content;
}
.save-badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-md) * 0.438);
  right: calc(-1 * var(--nxt-space-md) * 0.438);
}
.nx-sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
